// Trendyol magazasi baglama.
//
// Etsy'den farkli olarak OAuth yok: Trendyol Satici Paneli > Hesap Bilgilerim >
// Entegrasyon Bilgileri ekranindaki Satici ID, API Key ve API Secret girilir.
// Bilgiler KAYDEDILMEDEN once sunucu Trendyol'a gercek bir istek atip dogrular;
// boylece listede "bagli" gorunen ama calismayan magaza olusmaz.

import { useState } from "react";
import { Loader2, Plus, ShieldCheck, AlertCircle, Info, Activity, Trash2, Pencil, Check, X } from "lucide-react";
import { authHeaders, getBackendUrl } from "../../lib/http";
import { useShopStore } from "../../store/shop.store";

const VERTICALS: Array<{ key: string; label: string }> = [
  { key: "apparel", label: "Tekstil (tişört, sweat, hoodie)" },
  { key: "giftware", label: "Hediyelik (kupa, paspas, süsleme)" },
  { key: "jewelry", label: "Takı (kolye, yüzük, bileklik)" },
];

export function TrendyolShopCard() {
  const { shops, loadShops } = useShopStore();
  const trendyolShops = shops.filter((s) => s.platform === "trendyol");

  const [open, setOpen] = useState(false);
  const [sellerId, setSellerId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [shopName, setShopName] = useState("");
  const [vertical, setVertical] = useState("apparel");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  // Magaza adi duzenleme — Trendyol'da ad bizim etiketimiz, platformdan gelmiyor
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");

  async function rename(id: string) {
    const name = renameValue.trim();
    if (!name) { setRenamingId(null); return; }
    setError(null);
    try {
      const res = await fetch(`${getBackendUrl()}/api/etsy/shops/${id}`, {
        method: "PATCH", headers: authHeaders(), body: JSON.stringify({ shopName: name }),
      });
      if (!res.ok) throw new Error("Ad güncellenemedi");
      setRenamingId(null);
      await loadShops();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Ad güncellenemedi");
    }
  }

  async function connect() {
    setBusy(true); setError(null); setMsg(null);
    try {
      const res = await fetch(`${getBackendUrl()}/api/etsy/shops/trendyol`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ sellerId: sellerId.trim(), apiKey: apiKey.trim(), apiSecret: apiSecret.trim(), shopName: shopName.trim(), vertical }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error([body.error, body.details].filter(Boolean).join(" — ") || "Bağlantı kurulamadı");
      setMsg(body.message || "Mağaza bağlandı");
      setSellerId(""); setApiKey(""); setApiSecret(""); setShopName("");
      setOpen(false);
      await loadShops();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Bağlantı kurulamadı");
    } finally {
      setBusy(false);
    }
  }

  async function test(id: string) {
    setTestingId(id); setError(null); setMsg(null);
    try {
      const res = await fetch(`${getBackendUrl()}/api/etsy/shops/${id}/test`, { method: "POST", headers: authHeaders() });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error([body.error, body.details].filter(Boolean).join(" — ") || "Bağlantı başarısız");
      setMsg(body.message || "Bağlantı çalışıyor");
      await loadShops();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Bağlantı başarısız");
    } finally {
      setTestingId(null);
    }
  }

  async function remove(id: string, name: string) {
    if (!confirm(`"${name}" mağazasının bağlantısı kaldırılsın mı?`)) return;
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${getBackendUrl()}/api/etsy/shops/${id}`, { method: "DELETE", headers: authHeaders() });
      if (!res.ok) throw new Error("Silinemedi");
      await loadShops();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Silinemedi");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <span className="flex h-5 w-5 items-center justify-center rounded bg-orange-500 text-[10px] font-bold text-white">t</span>
            Trendyol Mağazaları
          </h3>
          <p className="mt-0.5 text-xs text-gray-500">
            API Key / Secret ile bağlanır; Etsy'deki gibi OAuth ekranı yoktur.
          </p>
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1.5 rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-orange-600"
        >
          <Plus className="h-3.5 w-3.5" /> Trendyol Mağazası Ekle
        </button>
      </div>

      {trendyolShops.length > 0 && (
        <div className="mb-3 space-y-1.5">
          {trendyolShops.map((s) => (
            <div key={s.id} className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: s.isActive ? s.color || "#f97316" : "#d1d5db" }} />
              {renamingId === s.id ? (
                <span className="flex items-center gap-1">
                  <input
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") rename(s.id);
                      if (e.key === "Escape") setRenamingId(null);
                    }}
                    className="w-44 rounded border border-gray-300 px-1.5 py-0.5 text-sm"
                  />
                  <button onClick={() => rename(s.id)} className="rounded p-0.5 text-emerald-600 hover:bg-emerald-50"><Check className="h-3.5 w-3.5" /></button>
                  <button onClick={() => setRenamingId(null)} className="rounded p-0.5 text-gray-400 hover:bg-gray-100"><X className="h-3.5 w-3.5" /></button>
                </span>
              ) : (
                <button
                  onClick={() => { setRenamingId(s.id); setRenameValue(s.shopName); }}
                  title="Mağaza adını düzenle"
                  className="group flex items-center gap-1 text-sm font-medium text-gray-900 hover:text-orange-600"
                >
                  {s.shopName}
                  <Pencil className="h-2.5 w-2.5 text-gray-400 opacity-0 group-hover:opacity-100" />
                </button>
              )}
              <span className="rounded bg-white px-1.5 py-0.5 font-mono text-[10px] text-gray-500">#{s.externalShopId}</span>
              {s.lastError && (
                <span className="flex items-center gap-1 text-[10px] text-red-600" title={s.lastError}>
                  <AlertCircle className="h-3 w-3" /> hata
                </span>
              )}
              <button
                onClick={() => test(s.id)}
                disabled={testingId === s.id}
                className="ml-auto flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                {testingId === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Activity className="h-3 w-3" />}
                Bağlantıyı sına
              </button>
              <button
                onClick={() => remove(s.id, s.shopName)}
                className="rounded border border-red-200 bg-white p-1 text-red-500 hover:bg-red-50"
                title="Bağlantıyı kaldır"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="space-y-2 rounded-lg border border-orange-200 bg-orange-50/50 p-3">
          <p className="flex items-start gap-1.5 text-[11px] text-gray-600">
            <Info className="mt-0.5 h-3 w-3 shrink-0 text-orange-500" />
            Trendyol Satıcı Paneli → Hesap Bilgilerim → <b>Entegrasyon Bilgileri</b> ekranındaki
            Satıcı ID (Supplier ID), API Key ve API Secret değerlerini girin.
          </p>

          <div className="grid grid-cols-2 gap-2">
            <label className="text-[11px] font-medium text-gray-600">
              Satıcı ID *
              <input
                value={sellerId}
                onChange={(e) => setSellerId(e.target.value)}
                placeholder="123456"
                className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1.5 font-mono text-sm"
              />
            </label>
            <label className="text-[11px] font-medium text-gray-600">
              Mağaza adı
              <input
                value={shopName}
                onChange={(e) => setShopName(e.target.value)}
                placeholder="(boş bırakılırsa Trendyol {ID})"
                className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1.5 text-sm"
              />
            </label>
          </div>

          <label className="block text-[11px] font-medium text-gray-600">
            API Key *
            <input
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1.5 font-mono text-sm"
            />
          </label>

          <label className="block text-[11px] font-medium text-gray-600">
            API Secret *
            <input
              value={apiSecret}
              onChange={(e) => setApiSecret(e.target.value)}
              type="password"
              className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1.5 font-mono text-sm"
            />
          </label>

          <label className="block text-[11px] font-medium text-gray-600">
            Mağaza kategorisi (dikey)
            <select
              value={vertical}
              onChange={(e) => setVertical(e.target.value)}
              className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1.5 text-sm"
            >
              {VERTICALS.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
            </select>
          </label>

          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={connect}
              disabled={busy || !sellerId.trim() || !apiKey.trim() || !apiSecret.trim()}
              className="flex items-center gap-1.5 rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-orange-600 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
              Doğrula ve Bağla
            </button>
            <button onClick={() => setOpen(false)} className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50">
              Vazgeç
            </button>
            <span className="text-[10px] text-gray-400">Bilgiler önce Trendyol'a sorulur, doğrulanmadan kaydedilmez.</span>
          </div>
        </div>
      )}

      {error && (
        <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {error}
        </p>
      )}
      {msg && <p className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">{msg}</p>}

      {trendyolShops.length === 0 && !open && (
        <p className="text-xs text-gray-400">Bağlı Trendyol mağazası yok.</p>
      )}
    </div>
  );
}
